import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Bangle sizes: inner diameter.
const BANGLE_SIZES = [
  ["2.2", "2 1/8\"", "5.4 cm", "Petite / children"],
  ["2.4", "2 1/4\"", "5.7 cm", "Small"],
  ["2.6", "2 3/8\"", "6.0 cm", "Medium (most common)"],
  ["2.8", "2 1/2\"", "6.4 cm", "Large"],
  ["2.10", "2 5/8\"", "6.7 cm", "Extra large"],
];

// UK ring sizes: inner diameter.
const RING_SIZES = [
  ["J", "15.2 mm", "47.8 mm"],
  ["L", "15.9 mm", "50.0 mm"],
  ["N", "16.5 mm", "52.0 mm"],
  ["P", "17.2 mm", "54.0 mm"],
  ["R", "17.8 mm", "56.0 mm"],
];

const SizeTable = ({ title, headers, rows }) => (
  <div className="mb-6 last:mb-0" data-testid="size-table">
    <p className="text-xs uppercase tracking-[0.16em] text-charcoal font-semibold mb-3">{title}</p>
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
            {headers.map((h, i) => (
              <th key={h} className={`py-2 font-semibold ${i === 0 ? "pr-4" : "px-3 text-center"}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]} className="border-b border-gray-100">
              {row.map((cell, i) => (
                <td key={i} className={`py-2.5 ${i === 0 ? "pr-4 font-medium text-charcoal" : "px-3 text-center text-gray-600"}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DEFAULT_CARE = [
  "Keep away from perfume, hairspray, water and lotions - put your jewellery on last and take it off first.",
  "Wipe gently with a soft, dry cloth after wearing.",
  "Store each piece separately in its pouch or an airtight box to prevent tarnishing and scratches.",
];

export const ProductInfoSections = ({ product = {} }) => {
  const details = [
    ["Style", product.style],
    ["Plating", product.plating],
    ["Base metal", product.base_metal],
    ["Stones", product.stones],
    ["Occasion", (product.occasions || []).join(", ")],
  ].filter(([, v]) => v);
  const care = product.care ? [product.care] : DEFAULT_CARE;

  return (
    <div className="mt-8 pt-6 border-t border-gray-100 space-y-5" data-testid="product-info-sections">
      <div className="text-sm text-gray-600 leading-relaxed" data-testid="product-disclaimer">
        <span className="font-semibold text-charcoal">Please note:</span> this is imitation (fashion) jewellery.
        Colours and stone tones may vary slightly with screen settings and lighting.
      </div>

      {details.length > 0 && (
        <div className="text-sm text-gray-600 leading-relaxed" data-testid="product-details">
          <p className="text-xs uppercase tracking-[0.16em] text-charcoal font-semibold mb-2">Details</p>
          {details.map(([label, value]) => (
            <p key={label}><span className="font-semibold text-charcoal">{label}</span> — {value}</p>
          ))}
        </div>
      )}

      <div className="text-sm text-gray-600 leading-relaxed" data-testid="product-care">
        <p className="text-xs uppercase tracking-[0.16em] text-charcoal font-semibold mb-2">Jewellery Care</p>
        {care.map((line) => <p key={line}>{line}</p>)}
      </div>

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="size-guide" className="border-gray-200 border-b-0">
          <AccordionTrigger data-testid="acc-size-guide" className="justify-start gap-2 text-charcoal hover:text-pink text-[15px] font-medium">
            Size Guide
          </AccordionTrigger>
          <AccordionContent className="pt-2">
            <p className="text-sm text-gray-600 leading-relaxed mb-4" data-testid="size-guide-note">
              Measure the inside diameter of a bangle or ring that already fits you well and match it below.
              Between sizes? Choose the larger size for bangles. Necklaces, earrings and most tikkas are one size
              with adjustable dori or hooks.
            </p>
            <SizeTable title="Bangles & kadas" headers={["Size", "Inner diameter", "Centimetres", "Fit"]} rows={BANGLE_SIZES} />
            <SizeTable title="Rings (UK)" headers={["Size", "Diameter", "Circumference"]} rows={RING_SIZES} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
